import React, { FC, useMemo, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ScalarLineChart } from 'components/Charts';
import {
  useGetCompanyZanbatoAskPricing,
  useGetCompanyZanbatoBidPricing,
  useGetCompanyZanbatoClosedTransactions,
  useGetCompanyZanbatoPricing,
} from 'services/hooks/companies';
import { CardBody, CardTitle, CardTitleRow, OutlinedCard, SourceChip } from '../CompanyOverview.styles';
import {
  ChangeLabel,
  CurrentPrice,
  HeaderLeft,
  PeriodControls,
  RobustnessRow,
  SeriesToggleRow,
  StatBar,
  StatItem,
  StatLabel,
  StatValue,
  Subtitle,
  TabButton,
  TabRow,
  TxTable,
  TxTableCell,
  TxTableHead,
  TxTableRow,
} from './ZXIndexValueCard.styles';
import {
  buildAllSeries,
  buildTooltipExtras,
  buildTooltipFormatter,
  calculateEstimatedShares,
  computeHeaderStats,
  DEFAULT_ENABLED_IDS,
  filterByPeriod,
  formatCurrency,
  formatCurrencyStr,
  formatEstimatedShares,
  formatPricingDate,
  formatVolume,
  getAsOfRecord,
  getPeriodStartDate,
  getRobustnessInfo,
  Period,
  PERIODS,
  PeriodValue,
  ROBUSTNESS_METHODOLOGY,
  ROBUSTNESS_SCORE_RANGES,
  SERIES_CONFIGS,
  toNum,
} from './ZXIndexValueCard.utils';
import { EndDateMode, EndDateModeValue, ViewMode, ViewModeValue } from '../MutualFundMarksCard/MutualFundMarksCard.utils';

interface Props {
  companyId: number | undefined;
  measurementDate?: string;
  endDateMode: EndDateModeValue;
  navigateToValuations?: () => void;
}

const ZXIndexValueCard: FC<Props> = ({ companyId, measurementDate, endDateMode, navigateToValuations }) => {
  const [period, setPeriod] = useState<PeriodValue>(Period.Y1);
  const [view, setView] = useState<ViewModeValue>(ViewMode.Chart);
  const [enabledIds, setEnabledIds] = useState<Set<string>>(DEFAULT_ENABLED_IDS);
  const [robustnessModalOpen, setRobustnessModalOpen] = useState(false);
  const [pricingSort, setPricingSort] = useState<{ col: string; dir: 'asc' | 'desc' }>({ col: 'date', dir: 'desc' });
  const [txSort, setTxSort] = useState<{ col: string; dir: 'asc' | 'desc' }>({ col: 'date', dir: 'desc' });
  const [tableTab, setTableTab] = useState<'pricing' | 'transactions'>('pricing');

  const { data: pricingData, isLoading: pricingLoading } = useGetCompanyZanbatoPricing(companyId);
  const { data: askData, isLoading: askLoading } = useGetCompanyZanbatoAskPricing(companyId);
  const { data: bidData, isLoading: bidLoading } = useGetCompanyZanbatoBidPricing(companyId);
  const { data: txData, isLoading: txLoading } = useGetCompanyZanbatoClosedTransactions(companyId);

  const isLoading = pricingLoading || askLoading || bidLoading || txLoading;

  const allPricing = useMemo(() => pricingData?.pricing ?? [], [pricingData?.pricing]);
  const allAsk = useMemo(() => askData?.pricing ?? [], [askData?.pricing]);
  const allBid = useMemo(() => bidData?.pricing ?? [], [bidData?.pricing]);
  const allTransactions = useMemo(() => txData?.transactions ?? [], [txData?.transactions]);

  const latestDate = useMemo(() => {
    if (!allPricing.length) {
      return undefined;
    }
    return (
      [...allPricing].filter(i => i.report_period).sort((a, b) => b.report_period!.localeCompare(a.report_period!))[0]
        ?.report_period ?? undefined
    );
  }, [allPricing]);

  const effectiveEndDate = endDateMode === EndDateMode.Measurement && measurementDate ? measurementDate : latestDate;

  const filteredPricing = useMemo(() => {
    if (!effectiveEndDate || !allPricing.length) {
      return allPricing;
    }
    return filterByPeriod(allPricing, period, effectiveEndDate);
  }, [allPricing, period, effectiveEndDate]);

  const filteredAsk = useMemo(() => {
    if (!effectiveEndDate || !allAsk.length) {
      return allAsk;
    }
    return filterByPeriod(allAsk, period, effectiveEndDate);
  }, [allAsk, period, effectiveEndDate]);

  const filteredBid = useMemo(() => {
    if (!effectiveEndDate || !allBid.length) {
      return allBid;
    }
    return filterByPeriod(allBid, period, effectiveEndDate);
  }, [allBid, period, effectiveEndDate]);

  // Transactions use closing_date rather than report_period so filter inline
  const filteredTransactions = useMemo(() => {
    if (!allTransactions.length) {
      return allTransactions;
    }
    if (period === Period.All) {
      return allTransactions.filter(t => t.closing_date);
    }
    if (!effectiveEndDate) {
      return allTransactions;
    }
    const startDate = getPeriodStartDate(effectiveEndDate, period);
    return allTransactions.filter(t => {
      if (!t.closing_date) {
        return false;
      }
      if (t.closing_date > effectiveEndDate) {
        return false;
      }
      if (startDate && t.closing_date < startDate) {
        return false;
      }
      return true;
    });
  }, [allTransactions, period, effectiveEndDate]);

  const series = useMemo(
    () => buildAllSeries(enabledIds, filteredPricing, filteredAsk, filteredBid, filteredTransactions),
    [enabledIds, filteredPricing, filteredAsk, filteredBid, filteredTransactions]
  );

  const tooltipFormatter = useMemo(() => {
    const extras = buildTooltipExtras(allPricing, allAsk, allBid, allTransactions);
    return buildTooltipFormatter(extras);
  }, [allPricing, allAsk, allBid, allTransactions]);

  const asOfRecord = useMemo(() => getAsOfRecord(allPricing, effectiveEndDate), [allPricing, effectiveEndDate]);

  const { currentValue, change } = useMemo(
    () => computeHeaderStats(filteredPricing, effectiveEndDate),
    [filteredPricing, effectiveEndDate]
  );

  const toggleSeries = (id: string) => {
    setEnabledIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const robustness = getRobustnessInfo(asOfRecord?.robustness);
  const hasData = allPricing.length > 0 || allAsk.length > 0 || allBid.length > 0 || allTransactions.length > 0;

  return (
    <OutlinedCard variant="outlined">
      <CardBody>
        <CardTitleRow>
          <HeaderLeft>
            <CardTitle component="span">ZX Index Value</CardTitle>
            {asOfRecord?.zx_index_value != null && (
              <Tooltip
                arrow
                title={
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: 2, fontSize: '0.8rem' }}>ZX Index Value (Zanbato)</div>
                    <div style={{ fontSize: '0.78rem' }}>{formatCurrencyStr(asOfRecord.zx_index_value)}</div>
                    {asOfRecord.report_period && (
                      <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.65)', marginTop: 4 }}>
                        As of {formatPricingDate(asOfRecord.report_period)}
                      </div>
                    )}
                  </div>
                }>
                <InfoOutlinedIcon sx={{ fontSize: '0.9rem', color: 'text.disabled', cursor: 'default', ml: 0.5 }} />
              </Tooltip>
            )}
            {hasData && (
              <>
                <CurrentPrice>{formatCurrency(currentValue)}</CurrentPrice>
                {change && (
                  <ChangeLabel positive={change.abs >= 0}>
                    {change.abs >= 0 ? '+' : ''}
                    {change.abs.toFixed(2)} ({change.abs >= 0 ? '+' : ''}
                    {change.pct.toFixed(2)}%)
                  </ChangeLabel>
                )}
              </>
            )}
          </HeaderLeft>

          {hasData && (
            <PeriodControls>
              <ToggleButtonGroup
                value={period}
                exclusive
                size="small"
                onChange={(_e, v: PeriodValue | null) => {
                  if (v) {
                    setPeriod(v);
                  }
                }}>
                {PERIODS.map(p => (
                  <ToggleButton key={p} value={p}>
                    {p}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <ToggleButtonGroup
                value={view}
                exclusive
                size="small"
                onChange={(_e, v: ViewModeValue | null) => {
                  if (v) {
                    setView(v);
                  }
                }}>
                <ToggleButton value={ViewMode.Chart}>Chart</ToggleButton>
                <ToggleButton value={ViewMode.Table}>Table</ToggleButton>
              </ToggleButtonGroup>
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon sx={{ fontSize: '0.7rem !important' }} />}
                href="https://zanbato.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', height: 26, py: 0 }}>
                Zanbato
              </Button>
            </PeriodControls>
          )}

          {!hasData && <SourceChip label="Zanbato" size="small" chipColor="blue" />}

          {navigateToValuations && !isLoading && hasData && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={navigateToValuations}
              sx={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', height: 26, py: 0, ml: 1 }}>
              Use in Valuation
            </Button>
          )}
        </CardTitleRow>

        {hasData && (
          <Subtitle>
            <span>ZX Index Value (Zanbato){effectiveEndDate ? ` as of ${effectiveEndDate}` : ''}</span>
            {robustness && (
              <>
                <Chip
                  label={robustness.label}
                  size="small"
                  color={robustness.color}
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, borderRadius: 1 }}
                />
                <Tooltip
                  arrow
                  title={
                    <div style={{ maxWidth: 280 }}>
                      <div style={{ fontWeight: 700, marginBottom: 4, fontSize: '0.8rem' }}>
                        {robustness.label}: {robustness.confidence}
                      </div>
                      <div style={{ fontSize: '0.75rem', lineHeight: 1.55, marginBottom: 8 }}>
                        {robustness.scoreDescription}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)', fontStyle: 'italic' }}>
                        Click to see all score ranges
                      </div>
                    </div>
                  }>
                  <InfoOutlinedIcon
                    onClick={() => setRobustnessModalOpen(true)}
                    sx={{ fontSize: '0.85rem', color: 'text.disabled', cursor: 'pointer' }}
                  />
                </Tooltip>
              </>
            )}
          </Subtitle>
        )}

        {isLoading && <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />}

        {!isLoading && !hasData && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
            No Zanbato pricing data available for this company.
          </Typography>
        )}

        {!isLoading && hasData && (
          <>
            {view === ViewMode.Chart && (
              <>
                <SeriesToggleRow>
                  {SERIES_CONFIGS.map(s => (
                    <Chip
                      key={s.id}
                      label={s.label}
                      size="small"
                      onClick={() => toggleSeries(s.id)}
                      variant={enabledIds.has(s.id) ? 'filled' : 'outlined'}
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        backgroundColor: enabledIds.has(s.id) ? s.color : 'transparent',
                        borderColor: s.color,
                        color: enabledIds.has(s.id) ? '#fff' : s.color,
                        '&:hover': {
                          backgroundColor: enabledIds.has(s.id) ? s.color : `${s.color}22`,
                        },
                      }}
                    />
                  ))}
                </SeriesToggleRow>

                <ScalarLineChart
                  series={series}
                  notMerge
                  smooth
                  showPoints={false}
                  style={{ height: 280 }}
                  config={{
                    legend: { show: false },
                    xAxis: { formatter: v => formatPricingDate(String(v)) },
                    yAxis: { formatter: v => `$${v}` },
                    options: {
                      tooltip: {
                        trigger: 'axis',
                        appendToBody: true,
                        formatter: tooltipFormatter,
                      },
                    },
                  }}
                />

                <StatBar>
                  <StatItem>
                    <StatLabel>ZX Index Value (Current)</StatLabel>
                    <StatValue>{formatCurrency(currentValue)}</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Volume</StatLabel>
                    <StatValue>{formatVolume(asOfRecord?.total_volume)}</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Robustness Score</StatLabel>
                    <StatValue>
                      {robustness ? (
                        <RobustnessRow>
                          <Chip
                            label={robustness.label}
                            size="small"
                            color={robustness.color}
                            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, borderRadius: 1 }}
                          />
                          <span style={{ fontSize: '0.82rem', fontWeight: 400 }}>{robustness.confidence}</span>
                        </RobustnessRow>
                      ) : (
                        '—'
                      )}
                    </StatValue>
                  </StatItem>
                </StatBar>
              </>
            )}

            {view === ViewMode.Table && (() => {
              const sortDir = (d: 'asc' | 'desc') => (d === 'asc' ? 1 : -1) as 1 | -1;
              const cmpNum = (a: number | null, b: number | null, d: 1 | -1) => {
                if (a === null && b === null) return 0;
                if (a === null) return 1;
                if (b === null) return -1;
                return (a - b) * d;
              };
              const togglePricingSort = (col: string) =>
                setPricingSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));
              const toggleTxSort = (col: string) =>
                setTxSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));
              const sortArrow = (isActive: boolean, dir: string) => {
                if (!isActive) return '↕';
                return dir === 'asc' ? '↑' : '↓';
              };
              const pIndicator = (col: string) => (
                <span style={{ marginLeft: 3, fontSize: '0.6rem', opacity: pricingSort.col === col ? 1 : 0.3 }}>
                  {sortArrow(pricingSort.col === col, pricingSort.dir)}
                </span>
              );
              const tIndicator = (col: string) => (
                <span style={{ marginLeft: 3, fontSize: '0.6rem', opacity: txSort.col === col ? 1 : 0.3 }}>
                  {sortArrow(txSort.col === col, txSort.dir)}
                </span>
              );
              const sortableTh: React.CSSProperties = { cursor: 'pointer', userSelect: 'none' };

              type PricingRow = {
                date: string; type: string;
                value: string; valueRaw: number | null;
                volume: string; volumeRaw: number | null;
                dwa: string; dwaRaw: number | null;
                simpleAvg: string; simpleAvgRaw: number | null;
                robustness: string; robustnessRaw: number | null;
              };
              const pricingRows: PricingRow[] = [];
              filteredPricing.filter(i => i.report_period).forEach(item => {
                pricingRows.push({
                  date: item.report_period!, type: 'ZX Current',
                  value: formatCurrencyStr(item.zx_index_value), valueRaw: toNum(item.zx_index_value),
                  volume: formatVolume(item.total_volume), volumeRaw: toNum(item.total_volume),
                  dwa: formatCurrencyStr(item.overall_dollar_weighted_average), dwaRaw: toNum(item.overall_dollar_weighted_average),
                  simpleAvg: formatCurrencyStr(item.overall_simple_average), simpleAvgRaw: toNum(item.overall_simple_average),
                  robustness: item.robustness != null ? `RS${item.robustness}` : '—', robustnessRaw: item.robustness ?? null,
                });
                pricingRows.push({
                  date: item.report_period!, type: 'ZX Trailing',
                  value: formatCurrencyStr(item.zx_index_value_trailing), valueRaw: toNum(item.zx_index_value_trailing),
                  volume: '—', volumeRaw: null, dwa: '—', dwaRaw: null,
                  simpleAvg: '—', simpleAvgRaw: null, robustness: '—', robustnessRaw: null,
                });
              });
              filteredAsk.filter(i => i.report_period).forEach(item => {
                pricingRows.push({
                  date: item.report_period!, type: 'Ask',
                  value: formatCurrencyStr(item.ask_zx_index_value), valueRaw: toNum(item.ask_zx_index_value),
                  volume: formatVolume(item.ask_volume), volumeRaw: toNum(item.ask_volume),
                  dwa: formatCurrencyStr(item.ask_dollar_weighted_average), dwaRaw: toNum(item.ask_dollar_weighted_average),
                  simpleAvg: formatCurrencyStr(item.ask_simple_average), simpleAvgRaw: toNum(item.ask_simple_average),
                  robustness: item.robustness != null ? `RS${item.robustness}` : '—', robustnessRaw: item.robustness ?? null,
                });
              });
              filteredBid.filter(i => i.report_period).forEach(item => {
                pricingRows.push({
                  date: item.report_period!, type: 'Bid',
                  value: formatCurrencyStr(item.bid_zx_index_value), valueRaw: toNum(item.bid_zx_index_value),
                  volume: formatVolume(item.bid_volume), volumeRaw: toNum(item.bid_volume),
                  dwa: formatCurrencyStr(item.bid_dollar_weighted_average), dwaRaw: toNum(item.bid_dollar_weighted_average),
                  simpleAvg: formatCurrencyStr(item.bid_simple_average), simpleAvgRaw: toNum(item.bid_simple_average),
                  robustness: item.robustness != null ? `RS${item.robustness}` : '—', robustnessRaw: item.robustness ?? null,
                });
              });

              const TYPE_ORDER = ['ZX Current', 'ZX Trailing', 'Ask', 'Bid'];
              const pd = sortDir(pricingSort.dir);
              pricingRows.sort((a, b) => {
                switch (pricingSort.col) {
                  case 'type': return (TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type)) * pd;
                  case 'value': return cmpNum(a.valueRaw, b.valueRaw, pd);
                  case 'volume': return cmpNum(a.volumeRaw, b.volumeRaw, pd);
                  case 'dwa': return cmpNum(a.dwaRaw, b.dwaRaw, pd);
                  case 'simpleAvg': return cmpNum(a.simpleAvgRaw, b.simpleAvgRaw, pd);
                  case 'robustness': return cmpNum(a.robustnessRaw, b.robustnessRaw, pd);
                  default: {
                    const dc = a.date.localeCompare(b.date) * pd;
                    return dc !== 0 ? dc : TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type);
                  }
                }
              });

              const td = sortDir(txSort.dir);
              const sortedTx = [...filteredTransactions].sort((a, b) => {
                switch (txSort.col) {
                  case 'security_type': return (a.security_type ?? '').localeCompare(b.security_type ?? '') * td;
                  case 'structure': return (a.structure ?? '').localeCompare(b.structure ?? '') * td;
                  case 'price': return cmpNum(toNum(a.price_per_share), toNum(b.price_per_share), td);
                  case 'total_value': return (a.total_value ?? '').localeCompare(b.total_value ?? '') * td;
                  case 'est_shares': {
                    const sa = calculateEstimatedShares(a.total_value, a.price_per_share);
                    const sb = calculateEstimatedShares(b.total_value, b.price_per_share);
                    const getMin = (s: typeof sa) => {
                      if (s == null) return null;
                      return s.type === 'range' ? s.min : s.value;
                    };
                    return cmpNum(getMin(sa), getMin(sb), td);
                  }
                  default: return (a.closing_date ?? '').localeCompare(b.closing_date ?? '') * td;
                }
              });

              return (
                <>
                  <TabRow>
                    <TabButton active={tableTab === 'pricing'} onClick={() => setTableTab('pricing')}>
                      Pricing History
                    </TabButton>
                    <TabButton active={tableTab === 'transactions'} onClick={() => setTableTab('transactions')}>
                      Closed Transactions
                    </TabButton>
                  </TabRow>

                  {tableTab === 'pricing' && (
                    pricingRows.length > 0 ? (
                      <TxTable>
                        <thead>
                          <TxTableRow>
                            <TxTableHead style={sortableTh} onClick={() => togglePricingSort('date')}>Date{pIndicator('date')}</TxTableHead>
                            <TxTableHead style={sortableTh} onClick={() => togglePricingSort('type')}>Type{pIndicator('type')}</TxTableHead>
                            <TxTableHead style={sortableTh} onClick={() => togglePricingSort('value')}>ZX Value{pIndicator('value')}</TxTableHead>
                            <TxTableHead style={sortableTh} onClick={() => togglePricingSort('volume')}>Volume{pIndicator('volume')}</TxTableHead>
                            <TxTableHead style={sortableTh} onClick={() => togglePricingSort('dwa')}>DWA{pIndicator('dwa')}</TxTableHead>
                            <TxTableHead style={sortableTh} onClick={() => togglePricingSort('simpleAvg')}>Simple Avg{pIndicator('simpleAvg')}</TxTableHead>
                            <TxTableHead style={sortableTh} onClick={() => togglePricingSort('robustness')}>Robustness{pIndicator('robustness')}</TxTableHead>
                          </TxTableRow>
                        </thead>
                        <tbody>
                          {pricingRows.map((row, i) => (
                            <TxTableRow key={`${row.date}-${row.type}-${i}`}>
                              <TxTableCell>{formatPricingDate(row.date)}</TxTableCell>
                              <TxTableCell>{row.type}</TxTableCell>
                              <TxTableCell>{row.value}</TxTableCell>
                              <TxTableCell>{row.volume}</TxTableCell>
                              <TxTableCell>{row.dwa}</TxTableCell>
                              <TxTableCell>{row.simpleAvg}</TxTableCell>
                              <TxTableCell>{row.robustness}</TxTableCell>
                            </TxTableRow>
                          ))}
                        </tbody>
                      </TxTable>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
                        No pricing data available for this period.
                      </Typography>
                    )
                  )}

                  {tableTab === 'transactions' && (
                    filteredTransactions.length > 0 ? (
                      <TxTable>
                        <thead>
                          <TxTableRow>
                            <TxTableHead style={sortableTh} onClick={() => toggleTxSort('date')}>Closing Date{tIndicator('date')}</TxTableHead>
                            <TxTableHead style={sortableTh} onClick={() => toggleTxSort('security_type')}>Security Type{tIndicator('security_type')}</TxTableHead>
                            <TxTableHead style={sortableTh} onClick={() => toggleTxSort('structure')}>Structure{tIndicator('structure')}</TxTableHead>
                            <TxTableHead style={sortableTh} onClick={() => toggleTxSort('price')}>Price / Share{tIndicator('price')}</TxTableHead>
                            <TxTableHead style={sortableTh} onClick={() => toggleTxSort('total_value')}>Est. Transaction Size{tIndicator('total_value')}</TxTableHead>
                            <TxTableHead style={sortableTh} onClick={() => toggleTxSort('est_shares')}>Est. Shares{tIndicator('est_shares')}</TxTableHead>
                          </TxTableRow>
                        </thead>
                        <tbody>
                          {sortedTx.map((t, i) => {
                            const shares = calculateEstimatedShares(t.total_value, t.price_per_share);
                            return (
                              <TxTableRow key={t.id ?? i}>
                                <TxTableCell>{t.closing_date ? formatPricingDate(t.closing_date) : '—'}</TxTableCell>
                                <TxTableCell>{t.security_type ?? '—'}</TxTableCell>
                                <TxTableCell>{t.structure ?? '—'}</TxTableCell>
                                <TxTableCell>{formatCurrencyStr(t.price_per_share)}</TxTableCell>
                                <TxTableCell>{t.total_value ?? '—'}</TxTableCell>
                                <TxTableCell>{formatEstimatedShares(shares)}</TxTableCell>
                              </TxTableRow>
                            );
                          })}
                        </tbody>
                      </TxTable>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
                        No closed transactions available for this period.
                      </Typography>
                    )
                  )}
                </>
              );
            })()}
          </>
        )}
      </CardBody>
      <Dialog open={robustnessModalOpen} onClose={() => setRobustnessModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>ZXData Robustness Score</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 3 }}>
            {ROBUSTNESS_METHODOLOGY}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
              display: 'block',
              mb: 1.5,
            }}>
            Score Interpretations
          </Typography>
          {ROBUSTNESS_SCORE_RANGES.map(range => (
            <div key={range.label} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 3,
                  minWidth: 68,
                  paddingTop: 2,
                }}>
                <Chip
                  label={`RS${range.label}`}
                  size="small"
                  color={range.color}
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, borderRadius: 1 }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.65rem', color: 'text.secondary', textAlign: 'center', lineHeight: 1.3 }}>
                  {range.confidence}
                </Typography>
              </div>
              <Typography variant="body2" sx={{ lineHeight: 1.65, fontSize: '0.82rem', pt: 0.25 }}>
                {range.description}
              </Typography>
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setRobustnessModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </OutlinedCard>
  );
};

export { ZXIndexValueCard };
